/**
 * O detalhe de um lugar (`StateDetail` de uma UF ou do Exterior, `ZZ`; §14.4) para os cartões de município (§8.4) e do
 * Exterior (§8.5): pedido pela ação `stateDetail` ao abrir e a cada `seq` novo ao vivo (o `FeedClient` deduplica e
 * guarda em cache). Fora do ao vivo não pede nem devolve nada (o detalhe do instante não existe).
 *
 * - O último detalhe bom do mesmo lugar fica enquanto o novo não chega, e também se ele falhar (o cartão não pisca para
 *   "carregando" nem para o erro com um dado válido na mão).
 * - Uma resposta atrasada do MESMO lugar serve (o seq seguinte pode ter chegado antes dela); de outro lugar, não.
 * - Nunca troca um detalhe mais novo por um mais velho que chegou depois.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { PlaceCode } from '../../contracts/base';
import type { StateDetail } from '../../contracts/municipal';
import { stateDetail } from '../../state/actions';

export type DetailState =
  | { place: PlaceCode; status: 'loading'; value: StateDetail | null }
  | { place: PlaceCode; status: 'ok'; value: StateDetail }
  | { place: PlaceCode; status: 'error'; value: StateDetail | null };

export function useStateDetail(place: PlaceCode | null, live: boolean, seq: number | null): DetailState | null {
  const [detail, setDetail] = useState<DetailState | null>(null);
  /** O lugar do último pedido; null depois de desmontar. */
  const asked = useRef<PlaceCode | null>(null);
  useEffect(
    () => () => {
      asked.current = null;
    },
    [],
  );
  useEffect(() => {
    if (place === null || !live || seq === null) return;
    asked.current = place;
    setDetail((d) => (d && d.place === place ? d : { place, status: 'loading', value: null }));
    stateDetail(place, 'president')
      .catch(() => null)
      .then((v) => {
        if (asked.current !== place) return;
        setDetail((d) => {
          const prev = d && d.place === place ? d.value : null;
          if (!v) return { place, status: 'error', value: prev };
          if (prev && prev.seq > v.seq) return d;
          return { place, status: 'ok', value: v };
        });
      });
  }, [place, seq, live]);
  // revendo a noite, nunca o detalhe de agora (o último carregado ao vivo): os cartões caem nas colunas do instante
  return live && detail && detail.place === place ? detail : null;
}
